import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

// Sun azimuth angles for Paris (northern hemisphere)
const SUN_CONFIG = {
  'Matin': { azimuth: 95, altitude: 20, label: 'Matin', icon: '↗' },
  'Midi': { azimuth: 180, altitude: 60, label: 'Midi', icon: '↑' },
  'Golden Hour': { azimuth: 255, altitude: 12, label: 'Golden Hour', icon: '↙' },
  'Nuit': { azimuth: null, altitude: 0, label: 'Nuit', icon: '✦' },
};

const CompassWidget = ({ value = 0, onChange, size = 200, momentJournee = null }) => {
  const svgRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [hoverAngle, setHoverAngle] = useState(null);
  const center = size / 2;
  const radius = size / 2 - 16;
  const innerRadius = radius - 22;
  const needleLength = innerRadius - 8;

  const angleToPosition = useCallback((angleDeg, r) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: center + r * Math.cos(rad), y: center + r * Math.sin(rad) };
  }, [center]);

  const getAngleFromEvent = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let angle = Math.atan2(clientY - cy, clientX - cx) * (180 / Math.PI) + 90;
    if (angle < 0) angle += 360;
    return Math.round(angle) % 360;
  }, []);

  const handlePointerDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    const angle = getAngleFromEvent(e);
    onChange(angle);
  }, [getAngleFromEvent, onChange]);

  const handlePointerMove = useCallback((e) => {
    const angle = getAngleFromEvent(e);
    if (isDragging) {
      onChange(angle);
    } else {
      setHoverAngle(angle);
    }
  }, [isDragging, getAngleFromEvent, onChange]);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      const onMove = (e) => handlePointerMove(e);
      const onUp = () => handlePointerUp();
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      window.addEventListener('touchmove', onMove);
      window.addEventListener('touchend', onUp);
      return () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        window.removeEventListener('touchmove', onMove);
        window.removeEventListener('touchend', onUp);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp]);

  // Compute sun & shadow geometry
  const sunShadow = useMemo(() => {
    const config = SUN_CONFIG[momentJournee];
    if (!config || config.azimuth === null) return null;

    // Sun azimuth is in compass degrees (0=N, 90=E, 180=S, 270=W)
    const sunAngle = config.azimuth;
    // Shadow falls opposite to sun
    const shadowAngle = (sunAngle + 180) % 360;
    // Shadow length inversely proportional to altitude (low sun = long shadow)
    const shadowLength = Math.max(0.3, 1 - (config.altitude / 90));
    // Shadow spread angle (wider when sun is lower)
    const spreadDeg = 25 + (1 - config.altitude / 90) * 20;

    return { sunAngle, shadowAngle, shadowLength, spreadDeg, altitude: config.altitude };
  }, [momentJournee]);

  const cardinals = [
    { label: 'N', angle: 0, color: '#ef4444' },
    { label: 'E', angle: 90, color: '#6b7280' },
    { label: 'S', angle: 180, color: '#6b7280' },
    { label: 'O', angle: 270, color: '#6b7280' },
  ];

  const tickMarks = Array.from({ length: 72 }, (_, i) => i * 5);
  const needleTip = angleToPosition(value, needleLength);
  const needleTail = angleToPosition(value + 180, needleLength * 0.35);

  const arcEnd = angleToPosition(value, innerRadius + 6);
  const arcStart = angleToPosition(0, innerRadius + 6);
  const largeArc = value > 180 ? 1 : 0;

  const transition = isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

  // Unique IDs for SVG defs
  const shadowGradientId = `shadow-grad-${size}`;
  const sunGlowId = `sun-glow-${size}`;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative select-none" style={{ width: size, height: size }}>
        <svg
          ref={svgRef}
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          onMouseDown={handlePointerDown}
          onMouseMove={(e) => !isDragging && handlePointerMove(e)}
          onMouseLeave={() => setHoverAngle(null)}
          onTouchStart={handlePointerDown}
          className="cursor-pointer"
          style={{ filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.12))' }}
          data-testid="compass-widget"
        >
          <defs>
            {/* Shadow cone gradient */}
            <radialGradient id={shadowGradientId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#0f172a" stopOpacity="0.7" />
              <stop offset="60%" stopColor="#1e293b" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#1e293b" stopOpacity="0" />
            </radialGradient>
            {/* Sun glow gradient */}
            <radialGradient id={sunGlowId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#f59e0b" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Background circle */}
          <circle cx={center} cy={center} r={radius} fill="#0f172a" stroke="#1e293b" strokeWidth="2" />

          {/* Shadow cone (rendered below everything else) */}
          {sunShadow && (() => {
            const { shadowAngle, shadowLength, spreadDeg } = sunShadow;
            const coneRadius = innerRadius * shadowLength;
            const leftAngle = shadowAngle - spreadDeg;
            const rightAngle = shadowAngle + spreadDeg;
            const tip = { x: center, y: center };
            const left = angleToPosition(leftAngle, coneRadius);
            const right = angleToPosition(rightAngle, coneRadius);
            // Build an arc path for the cone's outer edge
            const largeArcFlag = spreadDeg * 2 > 180 ? 1 : 0;
            return (
              <g style={{ transition }}>
                <path
                  d={`M ${tip.x} ${tip.y} L ${left.x} ${left.y} A ${coneRadius} ${coneRadius} 0 ${largeArcFlag} 1 ${right.x} ${right.y} Z`}
                  fill={`url(#${shadowGradientId})`}
                  opacity="0.6"
                  data-testid="shadow-cone"
                />
                {/* Shadow edge lines */}
                <line x1={tip.x} y1={tip.y} x2={left.x} y2={left.y} stroke="#475569" strokeWidth="0.5" opacity="0.4" strokeDasharray="3 3" />
                <line x1={tip.x} y1={tip.y} x2={right.x} y2={right.y} stroke="#475569" strokeWidth="0.5" opacity="0.4" strokeDasharray="3 3" />
              </g>
            );
          })()}

          {/* Sun glow on compass edge */}
          {sunShadow && (() => {
            const sunPos = angleToPosition(sunShadow.sunAngle, radius - 3);
            const glowSize = 14 + (sunShadow.altitude / 90) * 6;
            return (
              <g style={{ transition }}>
                {/* Outer glow */}
                <circle cx={sunPos.x} cy={sunPos.y} r={glowSize} fill={`url(#${sunGlowId})`} opacity="0.7" />
                {/* Sun dot */}
                <circle cx={sunPos.x} cy={sunPos.y} r="4" fill="#fbbf24" opacity="0.9" data-testid="sun-indicator" />
                <circle cx={sunPos.x} cy={sunPos.y} r="2.5" fill="#fef3c7" opacity="1" />
                {/* Sun rays */}
                {[0, 45, 90, 135, 180, 225, 270, 315].map((rayAngle) => {
                  const rad = (rayAngle * Math.PI) / 180;
                  const r1 = 6;
                  const r2 = 9;
                  return (
                    <line
                      key={rayAngle}
                      x1={sunPos.x + r1 * Math.cos(rad)}
                      y1={sunPos.y + r1 * Math.sin(rad)}
                      x2={sunPos.x + r2 * Math.cos(rad)}
                      y2={sunPos.y + r2 * Math.sin(rad)}
                      stroke="#fbbf24"
                      strokeWidth="1"
                      strokeLinecap="round"
                      opacity="0.6"
                    />
                  );
                })}
              </g>
            );
          })()}

          {/* Inner ring */}
          <circle cx={center} cy={center} r={innerRadius} fill="none" stroke="#334155" strokeWidth="1" />

          {/* Angle arc indicator */}
          {value > 0 && (
            <path
              d={`M ${arcStart.x} ${arcStart.y} A ${innerRadius + 6} ${innerRadius + 6} 0 ${largeArc} 1 ${arcEnd.x} ${arcEnd.y}`}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="3"
              strokeLinecap="round"
              opacity="0.6"
            />
          )}

          {/* Tick marks */}
          {tickMarks.map((angle) => {
            const isMajor = angle % 30 === 0;
            const isCardinal = angle % 90 === 0;
            const outerR = radius - 2;
            const innerR = isMajor ? radius - (isCardinal ? 14 : 10) : radius - 6;
            const p1 = angleToPosition(angle, outerR);
            const p2 = angleToPosition(angle, innerR);
            return (
              <line
                key={angle}
                x1={p1.x} y1={p1.y}
                x2={p2.x} y2={p2.y}
                stroke={isCardinal ? '#94a3b8' : isMajor ? '#475569' : '#334155'}
                strokeWidth={isCardinal ? 2 : 1}
              />
            );
          })}

          {/* Cardinal labels */}
          {cardinals.map(({ label, angle, color }) => {
            const pos = angleToPosition(angle, innerRadius - 14);
            return (
              <text
                key={label}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill={color}
                fontSize={label === 'N' ? 15 : 12}
                fontWeight="700"
                fontFamily="'Outfit', sans-serif"
                style={{ userSelect: 'none' }}
              >
                {label}
              </text>
            );
          })}

          {/* Degree labels at 30 intervals */}
          {[30, 60, 120, 150, 210, 240, 300, 330].map((angle) => {
            const pos = angleToPosition(angle, innerRadius - 14);
            return (
              <text
                key={angle}
                x={pos.x}
                y={pos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#475569"
                fontSize={8}
                fontFamily="'Outfit', sans-serif"
                style={{ userSelect: 'none' }}
              >
                {angle}°
              </text>
            );
          })}

          {/* Needle - south part */}
          <line
            x1={center} y1={center}
            x2={needleTail.x} y2={needleTail.y}
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ transition }}
          />

          {/* Needle - north part */}
          <line
            x1={center} y1={center}
            x2={needleTip.x} y2={needleTip.y}
            stroke="#ef4444"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ transition }}
          />

          {/* Needle tip arrowhead */}
          {(() => {
            const tipAngle = ((value - 90) * Math.PI) / 180;
            const arrowSize = 7;
            const tx = needleTip.x;
            const ty = needleTip.y;
            const left = {
              x: tx - arrowSize * Math.cos(tipAngle - 0.4),
              y: ty - arrowSize * Math.sin(tipAngle - 0.4),
            };
            const right = {
              x: tx - arrowSize * Math.cos(tipAngle + 0.4),
              y: ty - arrowSize * Math.sin(tipAngle + 0.4),
            };
            return (
              <polygon
                points={`${tx},${ty} ${left.x},${left.y} ${right.x},${right.y}`}
                fill="#ef4444"
                style={{ transition }}
              />
            );
          })()}

          {/* Center dot */}
          <circle cx={center} cy={center} r="5" fill="#f8fafc" stroke="#334155" strokeWidth="1.5" />
          <circle cx={center} cy={center} r="2" fill="#3b82f6" />

          {/* Hover guide line */}
          {hoverAngle !== null && !isDragging && (
            <line
              x1={center} y1={center}
              x2={angleToPosition(hoverAngle, needleLength).x}
              y2={angleToPosition(hoverAngle, needleLength).y}
              stroke="#3b82f6"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.3"
            />
          )}
        </svg>
      </div>

      {/* Digital readout */}
      <div className="flex items-center gap-3 bg-slate-900 rounded-lg px-3 py-1.5 border border-slate-700">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium">Nord</span>
          <span className="text-base font-mono font-bold text-blue-400 tabular-nums" data-testid="compass-value">
            {value}°
          </span>
        </div>
        {sunShadow && (
          <>
            <div className="w-px h-4 bg-slate-700" />
            <div className="flex items-center gap-1.5" data-testid="sun-info">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-[10px] text-amber-400/80 font-medium">
                {SUN_CONFIG[momentJournee]?.label}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { CompassWidget };
